import { Children, isValidElement, useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import countries from '../auth/register/data/countries.json';

const normalize = value => String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().trim();
const textContent = children => Children.toArray(children).map(child => isValidElement(child) ? textContent(child.props.children) : child).join('');

// Keep the existing country values and change handlers (phone prefix, leagues, etc.).
export default function SearchableCountrySelect({ children, value = '', onChange, name = 'country',
  required, disabled, placeholder, className, id }) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language || 'fr';
  const options = useMemo(() => {
    let names;
    try { names = new Intl.DisplayNames([language], { type: 'region' }); } catch (_) {}
    const supplied = Children.toArray(children).filter(isValidElement).map(child => ({
      value: child.props.value, label: textContent(child.props.children),
    }));
    const items = supplied.length ? supplied : [{ value: '', label: placeholder || t('athleteForm.country') },
      ...countries.map(country => ({ value: country.name, label: `${country.flag} ${country.name}` }))];
    return items.map(item => {
      const country = countries.find(country => country.name === item.value);
      return { ...item, search: normalize(`${item.label} ${country?.code || ''} ${country ? names?.of(country.code) || '' : ''}`) };
    });
  }, [children, language, placeholder, t]);
  const label = placeholder || options.find(option => option.value === '')?.label || t('athleteForm.country');
  const selected = options.find(option => option.value === value) || (value ? { value, label: value } : null);
  return <Autocomplete
    id={id}
    className={className ? `${className}-searchable` : undefined}
    options={options}
    value={selected}
    disabled={disabled}
    autoHighlight
    openOnFocus
    isOptionEqualToValue={(option, selectedOption) => option.value === selectedOption.value}
    getOptionLabel={option => option.value ? option.label : ''}
    filterOptions={(items, { inputValue }) => items.filter(item => item.search.includes(normalize(inputValue)))}
    onChange={(_, option) => onChange?.({ target: { name, value: option?.value || '', type: 'select-one' } })}
    noOptionsText={t('countryPicker.empty')}
    clearText={t('countryPicker.clear')}
    openText={t('countryPicker.open')}
    closeText={t('countryPicker.close')}
    renderOption={(props, option) => <li {...props} key={option.value}>{option.label}</li>}
    renderInput={params => <TextField {...params} name={name} required={required}
      placeholder={label} inputProps={{ ...params.inputProps, 'aria-label': label }} size="small" />}
    sx={{ width: '100%', minWidth: 0, '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#fff', minHeight: 46 },
      '& .MuiAutocomplete-input': { width: '0 !important', minWidth: '30px !important', border: '0 !important', boxShadow: 'none !important', margin: '0 !important' } }}
  />;
}
